import urls from "@/constants/urls";
import axios from "axios";

/**
 * @typedef {Object} PaymentSystemListResponse
 * @property {PaymentSystemItem[]} data - An array of payment system items.
 * @property {boolean} [needs_country] - true when the passed legal entity has no
 *   country set; `data` is empty and the caller should prompt for one.
 * @property {string} [country] - resolved legal-entity country (ISO alpha-2),
 *   present when `legal_entity_id` was supplied.
 */

/**
 * @typedef {Object} PaymentSystemItem
 * @property {number} id - The unique identifier for the payment system item.
 * @property {string} system_name - The name of the payment system.
 * @property {string} label - The label for the payment system item.
 * @property {string} description - A description of the payment system item.
 * @property {string} logo - The URL or path to the logo of the payment system.
 * @property {boolean} recurring - Indicates whether the payment system supports recurring payments.
 * @property {"charidy"|"external"|"stripe"} [onboarding_kind] - catalog placement; present when resolved for a legal entity.
 * @property {"available"|"needs_manager"|"unavailable_country"} [availability] - resolved state for the legal entity's country.
 * @property {Array<"onboard"|"connect">} [flows] - ways this system can be added for the legal entity.
 */

export const usePaymentSystemListApi = (oid) => {
  /**
   * @param params {Object} - query params. Pass `{ legal_entity_id }` to get
   *   the list resolved (availability / flows / onboarding_kind) for that legal
   *   entity's country; omit it for the plain catalogue.
   * @returns {Promise<PaymentSystemListResponse>}
   */
  async function getPaymentSystemList(params = {}) {
    const url = urls.paymentSystemList(oid);
    return axios.get(url, { params }).then((r) => r.data);
  }

  return { getPaymentSystemList };
};
